import { Camera, Loader2, Upload, UserPlus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import { getAllRoles } from '../Onboarding/onboardingApi';
import { getAllUsers } from '../OrgManagement/employeeApiService';
import { employeeApi } from '../ShiftManagement/employeeApi';
import { Button, Card, IconButton, PrimaryButton, ProfileImage, SearchBar } from '../ui';
import { inventoryApi } from './inventoryApi.js';
import { getPresignedImageUploadUrl, uploadImageToS3 } from './inventoryOnboardingApi.js';

const ItemMaintenance = () => {
  // Log screen view
  useScreenLogging('ItemMaintenance', {
    component: 'ItemMaintenance',
    screenType: 'item-maintenance',
  });

  const navigate = useNavigate();
  const { itemId } = useParams();
  
  const [inventoryItem, setInventoryItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  
  // Form fields
  const [reason, setReason] = useState('');
  const [assignedTechnician, setAssignedTechnician] = useState('');
  const [technicianAddress, setTechnicianAddress] = useState('');
  const [submissionDate, setSubmissionDate] = useState(null);
  const [scheduledReturnDate, setScheduledReturnDate] = useState(null);
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [responsibleRoleName, setResponsibleRoleName] = useState('');
  
  // Image upload
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isImageValid, setIsImageValid] = useState(true);
  
  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Roles and employees
  const [roles, setRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set());
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  
  // Preview mode
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (itemId) {
      fetchInventoryItem();
      fetchRoles();
    }
  }, [itemId]);

  // Prevent body scroll when employee dialog is open
  useEffect(() => {
    if (showEmployeeDialog) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [showEmployeeDialog]);

  const fetchInventoryItem = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await inventoryApi.getInventoryItem(itemId);

      if (result.success) {
        const itemData = result.data?.item || result.data;
        setInventoryItem(itemData);
      } else {
        setErrorMessage(result.message || 'Failed to fetch inventory item');
      }
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      setErrorMessage(error.message || 'Failed to fetch inventory item');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setRolesLoaded(false);
      const rolesData = await getAllRoles();
      
      // Filter out admin roles if user is not admin
      const filteredRoles = rolesData.filter(role => {
        const roleName = role.role_name?.toLowerCase() || '';
        const baseRole = role.base_role?.toLowerCase() || '';
        return roleName !== 'admin' && baseRole !== 'admin';
      });
      
      setRoles(filteredRoles);
      setRolesLoaded(true);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRolesLoaded(true);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setSubmitError(`Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`);
      setIsImageValid(false);
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setSubmitError('Please select a valid image file.');
      setIsImageValid(false);
      return;
    }

    setSelectedImage(file);
    setIsImageValid(true);
    setSubmitError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Check if camera is available
  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return { available: false, error: 'Unable to check camera availability' };
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.message.includes('No camera found')) {
        errorMessage = 'No camera found on this device.';
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  // Capture photo
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Convert data URL to File object
      const response = await fetch(photoDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });

      setSelectedImage(file);
      setImagePreview(photoDataUrl);
      setIsImageValid(true);
      setSubmitError(null);

      stopCamera();
    } catch (err) {
      console.error('Error capturing photo:', err);
      setSubmitError('Failed to capture photo. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  // Handle camera capture button click
  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const handleRoleChange = (e) => {
    const selectedDisplayName = e.target.value;
    setResponsiblePerson(selectedDisplayName);
    
    // Find the role_name for the selected display_name
    const selectedRole = roles.find(r => r.display_name === selectedDisplayName);
    if (selectedRole) {
      setResponsibleRoleName(selectedRole.role_name);
    }
  };

  const openEmployeeDialog = async () => {
    if (!responsiblePerson) {
      alert('Please select a role first before adding employees');
      return;
    }

    try {
      setShowEmployeeDialog(true);
      setAvailableEmployees([]);
      setIsLoadingEmployees(true);
      
      let usersRaw = [];
      let driversRaw = [];
      
      // Fetch all users
      try {
        usersRaw = await getAllUsers();
      } catch (err) {
        console.error('Failed to fetch all users:', err);
      }
      
      // Also fetch drivers separately if role is driver or employee
      const normalizedRole = responsibleRoleName.toLowerCase().trim();
      if (normalizedRole === 'driver' || normalizedRole === 'employee') {
        try {
          driversRaw = await driverApi.fetchAllDrivers();
        } catch (err) {
          console.error('Failed to fetch drivers:', err);
        }
      }
      
      // Combine users and drivers, removing duplicates
      const allUsersRaw = [...usersRaw, ...driversRaw];
      const uniqueUsers = [];
      const seenIds = new Set();
      
      allUsersRaw.forEach(user => {
        const id = user.user_id || user.employee_id || user.id;
        if (id && !seenIds.has(String(id))) {
          seenIds.add(String(id));
          uniqueUsers.push(user);
        }
      });
      
      // Filter by selected role (case-insensitive matching on both spec_role and role_name)
      const filteredUsers = uniqueUsers.filter(user => {
        const userSpecRole = (user.spec_role || '').toLowerCase().trim();
        const userRoleName = (user.role_name || '').toLowerCase().trim();
        
        // Match if spec_role or role_name matches the selected role
        return userSpecRole === normalizedRole || userRoleName === normalizedRole;
      });

      // Process employees data to include profile photos
      const processedEmployees = await Promise.all(
        filteredUsers.map(async (user) => {
          try {
            const imagePath = await employeeApi.getProfilePhoto(user.user_id);
            
            // Cache the profile image if we have an image path
            if (imagePath && imagePath.trim() !== '') {
              const userId = user.user_id || user.employee_id;
              
              // Check if already cached
              const cachedUrl = profileImageCache.get(userId);
              if (!cachedUrl) {
                // If it's already a URL, cache it directly
                if (imagePath.startsWith('http')) {
                  profileImageCache.set(userId, imagePath);
                } else {
                  // It's an S3 path, cache the path and fetch presigned URL in background
                  profileImageCache.setS3Path(userId, imagePath);
                  // Fetch presigned URL in background (don't await to avoid blocking)
                  userService.getS3PresignedUrl(imagePath)
                    .then(presignedUrl => {
                      if (presignedUrl) {
                        profileImageCache.set(userId, presignedUrl, imagePath);
                        console.log(`💾 Cached profile image URL for user ${userId}`);
                      }
                    })
                    .catch(error => {
                      console.warn(`⚠️ Failed to cache presigned URL for user ${userId}:`, error);
                    });
                }
              }
            }
            
            return {
              user_id: user.user_id || '',
              user_name: user.user_name || 'Unknown',
              employee_id: user.employee_id || user.user_id || '',
              spec_role: user.spec_role || 'Employee',
              display_name: user.user_name || 'Unknown',
              role: user.spec_role || 'Employee',
              image_path: imagePath || '',
            };
          } catch (photoError) {
            console.warn(`Failed to fetch profile photo for ${user.user_id}:`, photoError);
            return {
              user_id: user.user_id || '',
              user_name: user.user_name || 'Unknown',
              employee_id: user.employee_id || user.user_id || '',
              spec_role: user.spec_role || 'Employee',
              display_name: user.user_name || 'Unknown',
              role: user.spec_role || 'Employee',
              image_path: '',
            };
          }
        })
      );

      setAvailableEmployees(processedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setAvailableEmployees([]);
      alert('Failed to load employees');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };

  const confirmEmployeeSelection = () => {
    const selected = availableEmployees.filter(emp => 
      selectedEmployeeIds.has(emp.user_id)
    );
    
    // Add to selected employees (avoid duplicates)
    setSelectedEmployees(prev => {
      const existingIds = new Set(prev.map(e => e.user_id));
      const newEmployees = selected.filter(e => !existingIds.has(e.user_id));
      return [...prev, ...newEmployees];
    });
    
    setShowEmployeeDialog(false);
    setSelectedEmployeeIds(new Set());
    setEmployeeSearchTerm('');
  };

  const removeEmployee = (employeeId) => {
    setSelectedEmployees(prev => prev.filter(e => e.user_id !== employeeId));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedImage) {
      setIsImageValid(false);
      setSubmitError('Please upload a maintenance photo');
      return;
    }
    
    if (!reason.trim() || !assignedTechnician.trim() || !technicianAddress.trim() || 
        !submissionDate || !scheduledReturnDate || !responsiblePerson) {
      setSubmitError('Please fill all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Upload image first
      let maintenanceImageUrl = null;
      try {
        const presignResponse = await getPresignedImageUploadUrl({
          filename: `maintenance_${Date.now()}.jpg`,
          content_type: 'image/jpeg',
          for_maintenance: true,
        });

        // Convert file to blob
        const imageBlob = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result;
            const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
            resolve(blob);
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(selectedImage);
        });

        await uploadImageToS3(presignResponse.post_data, imageBlob);
        maintenanceImageUrl = presignResponse.s3_url;
      } catch (imageError) {
        console.warn('Failed to upload maintenance image:', imageError);
        // Continue without image
      }

      // Format dates
      const submissionDateISO = submissionDate 
        ? new Date(submissionDate).toISOString()
        : new Date().toISOString();
      const returnDateISO = new Date(scheduledReturnDate).toISOString();

      // Call startMaintenance API
      const result = await inventoryApi.startMaintenance({
        itemId: itemId,
        maintenanceReason: reason.trim(),
        assignedTechnician: assignedTechnician.trim(),
        technicianAddress: technicianAddress.trim(),
        responsibleRole: responsibleRoleName || responsiblePerson,
        responsiblePerson: responsiblePerson,
        submissionDate: submissionDateISO,
        scheduledReturnDate: returnDateISO,
        maintenanceImageUrl: maintenanceImageUrl,
        status: 'submitted',
      });

      if (result.success) {
        // Navigate back to item detail page
        navigate(`/inventory-item-detail/${itemId}`);
      } else {
        setSubmitError(result.message || 'Failed to submit maintenance request');
      }
    } catch (error) {
      console.error('Error submitting maintenance:', error);
      setSubmitError(error.message || 'Failed to submit maintenance request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEmployees = availableEmployees.filter(emp => {
    const search = employeeSearchTerm.toLowerCase();
    return (
      emp.user_name?.toLowerCase().includes(search) ||
      emp.display_name?.toLowerCase().includes(search) ||
      emp.employee_id?.toLowerCase().includes(search) ||
      emp.user_id?.toLowerCase().includes(search) ||
      emp.role?.toLowerCase().includes(search) ||
      emp.spec_role?.toLowerCase().includes(search)
    );
  });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setEmployeeSearchTerm(value);
  };

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
  };

  const handleSuggestionClick = (suggestion) => {
    setEmployeeSearchTerm(suggestion);
  };

  if (isLoading) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-4xl mx-auto h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading inventory item...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Item Maintenance
            </h1>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <X className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h3 className={typography.h3} style={{ color: '#DC2626', marginBottom: '0.5rem' }}>
                Error loading inventory item
              </h3>
              <p className="text-red-600 mb-4">{errorMessage}</p>
              <div className="flex gap-4 justify-center">
                <Button
                  type="button"
                  onClick={() => navigate(`/inventory-item-detail/${itemId}`)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  Go Back
                </Button>
                <PrimaryButton
                  type="button"
                  onClick={fetchInventoryItem}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Retry
                </PrimaryButton>
              </div>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Item Maintenance
          </h1>
        </div>

        {/* Maintenance Form */}
        <Card.Light className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Maintenance Photo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Maintenance Photo <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col items-center gap-4">
                  {/* Image Preview */}
                  <div className="relative">
                    {imagePreview ? (
                      <div className={`w-32 h-32 rounded-full border-2 overflow-hidden ${
                        isImageValid ? 'border-gray-300' : 'border-red-500'
                      }`}>
                        <img
                          src={imagePreview}
                          alt="Maintenance preview"
                          className="w-full h-full object-cover"
                        />
                        {!isPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setImagePreview(null);
                              setSelectedImage(null);
                              setIsImageValid(true);
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className={`w-32 h-32 rounded-full border-2 border-dashed flex items-center justify-center ${
                        isImageValid ? 'border-gray-300 bg-gray-50' : 'border-red-500 bg-red-50'
                      }`}>
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Upload Buttons */}
                  {!isPreview && (
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        style={{ color: '#ffffff' }}
                      >
                        <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                        <span style={{ color: '#ffffff' }}>Upload Image</span>
                      </Button>
                      <Button
                        type="button"
                        onClick={handleCameraCapture}
                        className={`flex items-center gap-2 ${
                          isCameraActive
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        style={{ color: '#ffffff' }}
                      >
                        <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                        <span style={{ color: '#ffffff' }}>Take Photo</span>
                      </Button>
                    </div>
                  )}

                  {/* Hidden Canvas for Photo Capture */}
                  <canvas
                    ref={canvasRef}
                    className="hidden"
                    width="640"
                    height="480"
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isPreview}
                  />
                </div>

                {/* Camera Preview */}
                {showCameraPreview && (
                  <div className="w-full mt-6">
                    <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                      <div className="text-center mb-3">
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                          Camera Preview
                        </h3>
                        {cameraError && (
                          <p className="text-red-600 text-sm mb-2">
                            {cameraError}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                          />

                          <div className="flex justify-center gap-3 mt-3">
                            <Button
                              type="button"
                              onClick={takePhoto}
                              disabled={isCapturing}
                              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                              style={{ color: '#ffffff' }}
                            >
                              {isCapturing ? 'Capturing...' : 'Capture Photo'}
                            </Button>

                            <Button
                              type="button"
                              onClick={stopCamera}
                              className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                              style={{ color: '#ffffff' }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for maintenance"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Assigned Technician/Vendor */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Assigned Technician/Vendor <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={assignedTechnician}
                  onChange={(e) => setAssignedTechnician(e.target.value)}
                  placeholder="Enter technician or vendor name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Address of Technician/Vendor */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address of the Technician/Vendor <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={technicianAddress}
                  onChange={(e) => setTechnicianAddress(e.target.value)}
                  placeholder="Enter address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  required
                  disabled={isPreview}
                />
              </div>

              {/* Date of Submission/Proposed */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date of Submission/Proposed <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={submissionDate ? new Date(submissionDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setSubmissionDate(new Date(e.target.value));
                    } else {
                      setSubmissionDate(null);
                    }
                  }}
                  onClick={(e) => {
                    if (!isPreview && e.target.showPicker) {
                      try {
                        e.target.showPicker();
                      } catch (err) {
                        // Fallback: let native behavior handle it
                        console.log('showPicker not available or requires user gesture');
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent manual typing but allow navigation keys
                    if (!['Tab', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  required
                  disabled={isPreview}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Scheduled Date for Return to Service */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Scheduled Date for Return to Service <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={scheduledReturnDate ? new Date(scheduledReturnDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setScheduledReturnDate(new Date(e.target.value));
                    }
                  }}
                  onClick={(e) => {
                    if (!isPreview && e.target.showPicker) {
                      try {
                        e.target.showPicker();
                      } catch (err) {
                        // Fallback: let native behavior handle it
                        console.log('showPicker not available or requires user gesture');
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent manual typing but allow navigation keys
                    if (!['Tab', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  required
                  disabled={isPreview}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Responsible Person */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Responsible Person <span className="text-red-500">*</span>
                </label>
                {!rolesLoaded ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-gray-500">Loading roles...</span>
                  </div>
                ) : (
                  <select
                    value={responsiblePerson}
                    onChange={handleRoleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={isPreview}
                  >
                    <option value=""></option>
                    {roles.map((role) => (
                      <option key={role.role_name} value={role.display_name}>
                        {role.display_name}
                      </option>
                    ))}
                  </select>
                )}
                
                {/* Add Employee Button */}
                {!isPreview && responsiblePerson && (
                  <Button
                    type="button"
                    onClick={openEmployeeDialog}
                    className="mt-3 bg-green-600 hover:bg-green-700 text-white flex items-center"
                    style={{ color: '#ffffff' }}
                  >
                    <UserPlus className="w-4 h-4 mr-2" style={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff' }}>Select Employees</span>
                  </Button>
                )}

                {/* Selected Employees */}
                {selectedEmployees.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Selected Employees:</p>
                    {selectedEmployees.map((employee) => (
                      <div
                        key={employee.user_id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 font-semibold">
                              {employee.user_name?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employee.user_name} (ID: {employee.employee_id})
                            </p>
                            <p className="text-xs text-gray-500">{employee.spec_role}</p>
                          </div>
                        </div>
                        {!isPreview && (
                          <button
                            type="button"
                            onClick={() => removeEmployee(employee.user_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                {isPreview ? (
                  <>
                    <Button
                      type="button"
                      onClick={() => setIsPreview(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
                    >
                      Edit
                    </Button>
                    <PrimaryButton
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      disabled={isSubmitting}
                      style={{ color: '#ffffff' }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin inline mr-2" style={{ color: '#ffffff' }} />
                          <span style={{ color: '#ffffff' }}>Submitting...</span>
                        </>
                      ) : (
                        <span style={{ color: '#ffffff' }}>Submit</span>
                      )}
                    </PrimaryButton>
                  </>
                ) : (
                  <Button
                    type="button"
                    onClick={() => setIsPreview(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    style={{ color: '#ffffff' }}
                  >
                    <span style={{ color: '#ffffff' }}>Check Preview</span>
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Card.Light>
      </div>

      {/* Employee Selection Dialog */}
      {showEmployeeDialog && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-[464px] max-h-[calc(90vh-2rem)] my-auto relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col">
            <div className="px-8 pt-6 pb-4 flex justify-between items-start flex-shrink-0">
              <div className="flex flex-col">
                <h3 className={typography.h3} style={{color: '#000000'}}>
                  Select Employees
                </h3>
                {selectedEmployeeIds.size > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedEmployeeIds.size} employee{selectedEmployeeIds.size !== 1 ? 's' : ''} selected
</span>
                )}
              </div>
              {/* Close Button */}
              <IconButton 
                onClick={() => {
                  setShowEmployeeDialog(false);
                  setSelectedEmployeeIds(new Set());
                  setEmployeeSearchTerm('');
                }}
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              />
            </div>

            {/* Dialog Content */}
            <div className="px-8 flex-1 flex flex-col min-h-0">
              {/* Search Bar in Dialog */}
              <div className="mb-6 flex-shrink-0">
                <SearchBar
                  placeholder="Search for employees..."
                  value={employeeSearchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                  suggestions={isLoadingEmployees ? [] : filteredEmployees.map(emp => emp.display_name || emp.user_name)}
                  onSuggestionClick={handleSuggestionClick}
                  className="w-full" 
                />
              </div>

              {/* Employee List */}
              <div className="space-y-3 flex-1 overflow-y-auto pr-2 min-h-0">
                {isLoadingEmployees ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
                    <p className="text-gray-600">Loading employees...</p>
                  </div>
                ) : filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee, index) => {
                    const isSelected = selectedEmployeeIds.has(employee.user_id);
                    const userId = employee.user_id || employee.employee_id;
                    
                    return (
                      <div key={userId || `employee-${index}`}>
                        {isSelected ? (
                          <Card.Gradient 
                            className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg"
                            onClick={() => handleEmployeeSelection(employee.user_id)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
                                <ProfileImage
                                  userId={employee.user_id}
                                  userName={employee.display_name || employee.user_name}
                                  imagePath={employee.image_path}
                                  size="md"
                                  showLoading={false}
                                />
                                <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                                  <div className="w-full text-black text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                                    {employee.display_name || employee.user_name}
                                  </div>
                                  <div className="w-full text-black text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                                    {employee.role || employee.spec_role} • ID: {employee.employee_id}
                                  </div>
                                </div>
                              </div>
                              <div 
                                data-property-1="check" 
                                className="w-6 h-6 relative flex-shrink-0 ml-3"
                              >
                                <div className="w-6 h-6 left-0 top-0 absolute bg-green-700 rounded-sm flex items-center justify-center">
                                  <svg 
                                    className="w-4 h-4 text-white"
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round" 
                                      strokeWidth={2} 
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </Card.Gradient>
                        ) : (
                          <Card.Dark 
                            className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg"
                            onClick={() => handleEmployeeSelection(employee.user_id)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
                                <ProfileImage
                                  userId={employee.user_id}
                                  userName={employee.display_name || employee.user_name}
                                  imagePath={employee.image_path}
                                  size="md"
                                  showLoading={false}
                                />
                                <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                                  <div className="w-full text-white text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                                    {employee.display_name || employee.user_name}
                                  </div>
                                  <div className="w-full text-gray-300 text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                                    {employee.role || employee.spec_role} • ID: {employee.employee_id}
                                  </div>
                                </div>
                              </div>
                              <div 
                                data-property-1="Default" 
                                className="w-6 h-6 relative flex-shrink-0 ml-3"
                              >
                                <div className="w-6 h-6 left-0 top-0 absolute bg-zinc-100 border-[1.60px] border-black/30 rounded-sm" />
                              </div>
                            </div>
                          </Card.Dark>
                        )}
                      </div>
                    );
                  })
                ) : employeeSearchTerm.length > 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    No employees found matching "{employeeSearchTerm}"
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No employees found with role: {responsiblePerson}
                  </div>
                )}
              </div>
            </div>

            {/* Dialog Actions */}
            <div className="px-8 pt-4 pb-6 flex gap-3 justify-center flex-shrink-0">
              <PrimaryButton 
                onClick={confirmEmployeeSelection}
                disabled={selectedEmployeeIds.size === 0}
              >
                Confirm {selectedEmployeeIds.size > 0 && `(${selectedEmployeeIds.size})`}
              </PrimaryButton>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default ItemMaintenance;

